import { CartAddEvent, CartErrorEvent } from '@theme/events';

const ROOT_SELECTOR = '[data-fibriya-shoppable-recipe-root]';
const DATA_SCRIPT_SELECTOR = 'script.fibriya-shoppable-recipe-data[type="application/json"]';
const CONFIG_SELECTOR = 'script[data-fibriya-shoppable-recipe-config][type="application/json"]';
const SUPPORTED_VERSION = 1;
const DEFAULT_QUANTITY = 1;
const FLY_TO_CART_STAGGER_MS = 70;
const productRequestCache = new Map();

const OFFICIAL_SHOPPABLE_TITLES = {
  de: 'Fibriya Zutaten direkt einkaufen',
  hu: 'Vásárold meg a Fibriya hozzávalókat',
  pl: 'Kup składniki Fibriya bezpośrednio',
  sk: 'Nakúpte ingrediencie Fibriya priamo',
  cs: 'Nakupte ingredience Fibriya přímo',
  cz: 'Nakupte ingredience Fibriya přímo',
};

const fallbackTranslations = {
  title: OFFICIAL_SHOPPABLE_TITLES.de,
  add_all: 'Alle Zutaten in den Warenkorb',
  add_selected: 'Ausgewählte Zutaten in den Warenkorb',
  out_of_stock: 'Ausverkauft',
  loading: 'Zutaten werden geladen',
  add_success: 'Zutaten wurden in den Warenkorb gelegt',
  product_error: 'Dieses Produkt konnte nicht geladen werden',
  cart_error: 'Die Zutaten konnten nicht in den Warenkorb gelegt werden',
  nothing_selected: 'Bitte wähle mindestens eine Zutat aus',
};

function normalizeLocale(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace('_', '-')
    .split('-', 1)[0];
}

function getOfficialShoppableTitle() {
  const locale = normalizeLocale(document.documentElement.lang);
  return OFFICIAL_SHOPPABLE_TITLES[locale] || OFFICIAL_SHOPPABLE_TITLES.de;
}

function withOfficialShoppableTitle(translations = {}) {
  return {
    ...translations,
    title: getOfficialShoppableTitle(),
  };
}

function readConfig() {
  const script = document.querySelector(CONFIG_SELECTOR);
  if (!script?.textContent) {
    return { translations: withOfficialShoppableTitle(fallbackTranslations) };
  }

  try {
    const parsed = JSON.parse(script.textContent);
    return {
      ...parsed,
      translations: withOfficialShoppableTitle({
        ...fallbackTranslations,
        ...(parsed.translations || {}),
      }),
    };
  } catch (error) {
    console.warn('Fibriya shoppable recipe config could not be parsed.', error);
    return { translations: withOfficialShoppableTitle(fallbackTranslations) };
  }
}

function normalizeRootUrl(rootUrl) {
  const fallbackRoot = window.Shopify?.routes?.root || '/';
  const root = typeof rootUrl === 'string' && rootUrl.trim() ? rootUrl.trim() : fallbackRoot;
  return root.endsWith('/') ? root : `${root}/`;
}

function normalizeQuantity(quantity) {
  const parsed = Number.parseInt(quantity, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : DEFAULT_QUANTITY;
}

function hasValidVariantId(variantId) {
  return variantId !== null && variantId !== undefined && String(variantId).trim() !== '';
}

function getPayloadScriptForRoot(root) {
  let node = root.nextElementSibling;

  while (node) {
    if (node.matches(ROOT_SELECTOR)) return null;
    if (node.matches(DATA_SCRIPT_SELECTOR)) return node;
    node = node.nextElementSibling;
  }

  return null;
}

function parsePayload(root) {
  const script = getPayloadScriptForRoot(root);
  if (!script?.textContent) return null;

  try {
    const payload = JSON.parse(script.textContent);
    if (payload?.version !== SUPPORTED_VERSION) return null;
    if (!Array.isArray(payload.products) || payload.products.length === 0) return null;
    return payload;
  } catch (error) {
    console.warn('Fibriya shoppable recipe payload could not be parsed.', error);
    return null;
  }
}

function normalizePayloadProduct(payloadProduct, index) {
  const handle = typeof payloadProduct?.product_handle === 'string' ? payloadProduct.product_handle.trim() : '';

  return {
    index,
    handle,
    variantId: payloadProduct?.variant_id,
    ingredientText: typeof payloadProduct?.ingredient_text === 'string' ? payloadProduct.ingredient_text : '',
    quantity: normalizeQuantity(payloadProduct?.quantity),
    defaultSelected: payloadProduct?.default_selected !== false,
    normalizedUrl: typeof payloadProduct?.normalized_url === 'string' ? payloadProduct.normalized_url : '',
    sourceUrl: typeof payloadProduct?.source_url === 'string' ? payloadProduct.source_url : '',
  };
}

function buildProductJsonUrl(rootUrl, handle) {
  return `${normalizeRootUrl(rootUrl)}products/${encodeURIComponent(handle)}.js`;
}

function fetchProduct(rootUrl, handle) {
  const cacheKey = `${normalizeRootUrl(rootUrl)}::${handle}`;

  if (!productRequestCache.has(cacheKey)) {
    productRequestCache.set(
      cacheKey,
      fetch(buildProductJsonUrl(rootUrl, handle), { headers: { Accept: 'application/json' } }).then((response) => {
        if (!response.ok) throw new Error(`Product request failed with status ${response.status}`);
        return response.json();
      })
    );
  }

  return productRequestCache.get(cacheKey);
}

function validateProductUrl(url, rootUrl, handle) {
  const fallback = `${normalizeRootUrl(rootUrl)}products/${encodeURIComponent(handle)}`;
  const candidate = typeof url === 'string' && url.trim() ? url.trim() : '';

  if (!candidate) return fallback;
  if (/^(javascript|data):/i.test(candidate)) return fallback;

  try {
    const parsed = new URL(candidate, window.location.origin);
    if (parsed.origin !== window.location.origin) return fallback;
    if (!parsed.pathname.includes('/products/')) return fallback;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (_error) {
    return fallback;
  }
}

function validateImageUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return '';

  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.protocol !== 'https:' && parsed.origin !== window.location.origin) return '';
    if (/^(javascript|data):/i.test(parsed.href)) return '';
    return parsed.href;
  } catch (_error) {
    return '';
  }
}

function getVariantImage(variant) {
  return variant?.featured_image?.src || variant?.featured_image || variant?.image || '';
}

function getProductImage(product) {
  if (typeof product?.featured_image === 'string') return product.featured_image;
  if (product?.featured_image?.src) return product.featured_image.src;
  if (Array.isArray(product?.images) && product.images[0]) return product.images[0];
  return '';
}

function findVariant(product, variantId) {
  if (!Array.isArray(product?.variants) || !hasValidVariantId(variantId)) return null;
  return product.variants.find((variant) => String(variant.id) === String(variantId)) || null;
}

function formatMoney(cents, config) {
  const amount = Number(cents);
  if (!Number.isFinite(amount)) return '';

  const currency = window.Shopify?.currency?.active;
  if (currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
      }).format(amount / 100);
    } catch (_error) {
      // Fall back to the Liquid money format below.
    }
  }

  const value = (amount / 100).toFixed(2).replace('.', ',');
  const moneyFormat = typeof config.moneyFormat === 'string' ? config.moneyFormat : '{{amount}}';
  return moneyFormat.replace(/{{\s*amount(?:_with_comma_separator)?\s*}}/g, value).replace(/<[^>]*>/g, '').trim();
}

function setText(element, text) {
  element.textContent = text || '';
  return element;
}

function createElement(tagName, className, text) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined) setText(element, text);
  return element;
}

function setMessage(state, message, type = '') {
  state.message.textContent = message || '';
  state.message.classList.toggle('is-error', type === 'error');
  state.message.classList.toggle('is-success', type === 'success');
  state.message.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');
}

function createProductState(payloadProduct, product, variant, config, status) {
  const available = Boolean(product && variant && variant.available);
  const title = product?.title || config.translations.product_error;
  const imageUrl = validateImageUrl(getVariantImage(variant) || getProductImage(product));
  const compareAtPrice = Number(variant?.compare_at_price || 0);
  const price = Number(variant?.price || 0);
  const showComparePrice = Number.isFinite(compareAtPrice) && Number.isFinite(price) && compareAtPrice > price;

  return {
    ...payloadProduct,
    product,
    variant,
    available,
    selected: available && payloadProduct.defaultSelected,
    title,
    imageUrl,
    productUrl: validateProductUrl(payloadProduct.normalizedUrl || payloadProduct.sourceUrl, config.rootUrl, payloadProduct.handle),
    priceText: variant ? formatMoney(variant.price, config) : '',
    compareAtPriceText: showComparePrice ? formatMoney(variant.compare_at_price, config) : '',
    variantTitle: variant?.title && variant.title !== 'Default Title' ? variant.title : '',
    status,
  };
}

async function resolveProduct(payloadProduct, config) {
  if (!payloadProduct.handle || !hasValidVariantId(payloadProduct.variantId)) {
    return createProductState(payloadProduct, null, null, config, 'error');
  }

  try {
    const product = await fetchProduct(config.rootUrl, payloadProduct.handle);
    const variant = findVariant(product, payloadProduct.variantId);

    if (!variant) return createProductState(payloadProduct, product, null, config, 'error');
    if (!variant.available) return createProductState(payloadProduct, product, variant, config, 'sold_out');

    return createProductState(payloadProduct, product, variant, config, 'available');
  } catch (_error) {
    return createProductState(payloadProduct, null, null, config, 'error');
  }
}

function createImageNode(productState) {
  if (!productState.imageUrl) {
    return createElement('div', 'fibriya-shoppable-recipe__image-placeholder');
  }

  const link = document.createElement('a');
  link.className = 'fibriya-shoppable-recipe__image-link';
  link.href = productState.productUrl;
  link.setAttribute('aria-label', productState.title);

  const image = document.createElement('img');
  image.className = 'fibriya-shoppable-recipe__image';
  image.src = productState.imageUrl;
  image.alt = productState.title;
  image.loading = 'lazy';

  link.append(image);
  return link;
}

function createProductCard(productState, state) {
  const card = createElement('article', 'fibriya-shoppable-recipe__card');
  card.classList.toggle('is-unavailable', !productState.available);

  const checkboxWrap = createElement('div', 'fibriya-shoppable-recipe__checkbox-wrap');
  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.className = 'fibriya-shoppable-recipe__checkbox';
  checkbox.id = `${state.idPrefix}-item-${productState.index}`;
  checkbox.checked = productState.selected;
  checkbox.disabled = !productState.available;
  checkbox.dataset.index = String(productState.index);
  checkbox.addEventListener('change', () => {
    productState.selected = checkbox.checked;
    updateCtaState(state);
  });
  checkboxWrap.append(checkbox);

  const body = createElement('div', 'fibriya-shoppable-recipe__body');
  const label = document.createElement('label');
  label.className = 'fibriya-shoppable-recipe__label';
  label.htmlFor = checkbox.id;

  const titleLink = document.createElement('a');
  titleLink.className = 'fibriya-shoppable-recipe__product-title';
  titleLink.href = productState.productUrl;
  titleLink.textContent = productState.title;
  titleLink.addEventListener('click', (event) => event.stopPropagation());
  label.append(titleLink);

  if (productState.ingredientText) {
    label.append(createElement('span', 'fibriya-shoppable-recipe__ingredient', productState.ingredientText));
  }

  if (productState.variantTitle) {
    label.append(createElement('span', 'fibriya-shoppable-recipe__variant', productState.variantTitle));
  }

  const priceRow = createElement('div', 'fibriya-shoppable-recipe__price-row');
  if (productState.priceText) {
    priceRow.append(createElement('span', 'fibriya-shoppable-recipe__price', productState.priceText));
  }
  if (productState.compareAtPriceText) {
    priceRow.append(createElement('span', 'fibriya-shoppable-recipe__compare-price', productState.compareAtPriceText));
  }
  if (priceRow.childElementCount) label.append(priceRow);

  if (productState.status === 'sold_out') {
    label.append(createElement('span', 'fibriya-shoppable-recipe__status', state.config.translations.out_of_stock));
  } else if (productState.status === 'error') {
    label.append(createElement('span', 'fibriya-shoppable-recipe__status', state.config.translations.product_error));
  }

  body.append(label);
  card.append(checkboxWrap, createImageNode(productState), body);
  productState.cardElement = card;
  return card;
}

function getSelectedProducts(state) {
  return state.products.filter((product) => product.available && product.selected);
}

function updateCtaState(state) {
  const selectedProducts = getSelectedProducts(state);
  const allSelected =
    state.products.length > 0 &&
    state.products.every((product) => product.available && product.selected);

  state.button.textContent = state.isSubmitting ? state.config.translations.loading : state.config.translations[allSelected ? 'add_all' : 'add_selected'];
  state.button.disabled = state.isSubmitting || selectedProducts.length === 0;

  if (!state.isSubmitting && state.products.every((product) => !product.available)) {
    setMessage(state, state.config.translations.product_error, 'error');
  } else if (!state.isSubmitting && selectedProducts.length === 0) {
    setMessage(state, state.config.translations.nothing_selected, 'error');
  } else if (!state.isSubmitting && state.message.textContent === state.config.translations.nothing_selected) {
    setMessage(state, '');
  }
}

function shouldAnimateFlyToCart() {
  return !window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
}

function animateFlyToCart(product, index = 0) {
  if (!shouldAnimateFlyToCart() || !customElements.get('fly-to-cart')) return;

  const cartIcon = document.querySelector('.header-actions__cart-icon');
  const source = product.cardElement?.querySelector('.fibriya-shoppable-recipe__image');

  if (!cartIcon || !source || !product.imageUrl) return;

  window.setTimeout(() => {
    const flyToCartElement = document.createElement('fly-to-cart');

    flyToCartElement.style.setProperty('background-image', `url(${product.imageUrl})`);
    flyToCartElement.source = source;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }, index * FLY_TO_CART_STAGGER_MS);
}

function animateSelectedProductsToCart(products) {
  products.forEach((product, index) => animateFlyToCart(product, index));
}

function collectCartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);
}

function getCartAddUrl(config) {
  if (typeof Theme !== 'undefined' && Theme.routes?.cart_add_url) return Theme.routes.cart_add_url;
  if (typeof config.cartAddUrl === 'string' && config.cartAddUrl) return config.cartAddUrl;
  return `${normalizeRootUrl(config.rootUrl)}cart/add.js`;
}

async function addSelectedItemsToCart(state) {
  const selectedProducts = getSelectedProducts(state);
  const items = selectedProducts.map((product) => ({ id: Number(product.variantId), quantity: product.quantity }));

  if (items.length === 0) {
    setMessage(state, state.config.translations.nothing_selected, 'error');
    updateCtaState(state);
    return;
  }

  state.isSubmitting = true;
  state.root.setAttribute('aria-busy', 'true');
  setMessage(state, '');
  updateCtaState(state);

  animateSelectedProductsToCart(selectedProducts);

  const body = {
    items,
    sections: collectCartSectionIds(),
    sections_url: window.location.pathname,
  };

  try {
    const response = await fetch(getCartAddUrl(state.config), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    let parsedResponse;
    try {
      parsedResponse = await response.json();
    } catch (_error) {
      throw new Error('Cart response was not valid JSON.');
    }

    if (!response.ok || parsedResponse.status || parsedResponse.errors) {
      throw new Error(parsedResponse.message || parsedResponse.description || 'Cart add failed.');
    }

    if (!parsedResponse.sections) {
      console.warn('Fibriya shoppable recipe cart response did not include sections.');
    }

    document.dispatchEvent(
      new CartAddEvent({}, state.idPrefix, {
        source: 'fibriya-shoppable-recipe',
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        sections: parsedResponse.sections,
      })
    );

    setMessage(state, state.config.translations.add_success, 'success');
  } catch (error) {
    document.dispatchEvent(new CartErrorEvent(state.idPrefix, state.config.translations.cart_error, error?.message || '', {}));
    setMessage(state, state.config.translations.cart_error, 'error');
  } finally {
    state.isSubmitting = false;
    state.root.setAttribute('aria-busy', 'false');
    updateCtaState(state);
  }
}

function renderModule(root, products, config) {
  const idPrefix = `fibriya-shoppable-recipe-${Math.random().toString(36).slice(2, 10)}`;
  const state = {
    root,
    products,
    config,
    idPrefix,
    isSubmitting: false,
    button: document.createElement('button'),
    message: createElement('div', 'fibriya-shoppable-recipe__message'),
  };

  root.classList.add('is-initialized');
  root.setAttribute('aria-busy', 'false');
  root.textContent = '';

  const inner = createElement('div', 'fibriya-shoppable-recipe__inner');
  const header = createElement('div', 'fibriya-shoppable-recipe__header');
  header.append(createElement('h2', 'fibriya-shoppable-recipe__title', config.translations.title));

  const grid = createElement('div', 'fibriya-shoppable-recipe__grid');
  products.forEach((product) => grid.append(createProductCard(product, state)));

  const actions = createElement('div', 'fibriya-shoppable-recipe__actions');
  state.button.type = 'button';
  state.button.className = 'fibriya-shoppable-recipe__button';
  state.button.addEventListener('click', () => addSelectedItemsToCart(state));
  state.message.setAttribute('aria-live', 'polite');
  actions.append(state.button, state.message);

  inner.append(header, grid, actions);
  root.append(inner);
  updateCtaState(state);
}

async function initializeRoot(root, config) {
  if (root.dataset.fibriyaShoppableRecipeInitialized === 'true') return;

  const payload = parsePayload(root);
  if (!payload) return;

  root.dataset.fibriyaShoppableRecipeInitialized = 'true';
  root.classList.add('is-initialized');
  root.setAttribute('aria-busy', 'true');
  root.textContent = '';

  const loading = createElement('div', 'fibriya-shoppable-recipe__message', config.translations.loading);
  loading.setAttribute('aria-live', 'polite');
  root.append(loading);

  const payloadProducts = payload.products.map(normalizePayloadProduct);
  const settled = await Promise.allSettled(payloadProducts.map((product) => resolveProduct(product, config)));
  const products = settled.map((result, index) =>
    result.status === 'fulfilled'
      ? result.value
      : createProductState(payloadProducts[index], null, null, config, 'error')
  );

  renderModule(root, products, config);
}

function initializeAll() {
  const config = readConfig();
  config.rootUrl = normalizeRootUrl(config.rootUrl);
  document.querySelectorAll(ROOT_SELECTOR).forEach((root) => initializeRoot(root, config));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
} else {
  initializeAll();
}
